import React, { ChangeEventHandler, MouseEventHandler, ReactNode } from 'react';

import Tooltip from 'component/Layout/Tooltip';
import { TooltipPosition } from 'component/Layout/Tooltip/types';

import classNames from 'classnames';

import { ConditionalWrapper } from 'utility/Check/ConditionalWrapper';
import { isTablet } from 'utility/Check/Device';

import styles from './Checkbox.module.scss';

export interface CheckboxProps {
    /** Object with settings for input */
    input: {
        name?: string;
        id: string;
        value?: string;
    };
    isChecked?: boolean;
    withUXSelection?: boolean;
    customClass?: {
        Wrapper?: string;
        Input?: string;
        Label?: string;
    };
    hasError?: boolean;
    isDisabled?: boolean;
    isOnlyDeselectable?: boolean;
    onClick?: MouseEventHandler<HTMLInputElement>;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    tooltipConfig?: {
        content?: ReactNode;
        placement?: TooltipPosition;
        align?: {
            offset: number[];
        };
    };
}

const Checkbox: React.FC<React.PropsWithChildren<CheckboxProps>> = ({
    input,
    isChecked = false,
    hasError = false,
    isDisabled = false,
    isOnlyDeselectable = false,
    withUXSelection = false,
    children,
    customClass,
    onClick,
    onChange,
    tooltipConfig,
}) => {
    return (
        <ConditionalWrapper
            condition={!!tooltipConfig?.content}
            wrapper={(children: React.ReactElement) => (
                <Tooltip
                    placement={tooltipConfig?.placement}
                    content={tooltipConfig?.content}
                    align={tooltipConfig?.align}
                >
                    {children}
                </Tooltip>
            )}
        >
            <div
                className={classNames(styles.Checkbox, customClass?.Wrapper, {
                    [styles.Tablet]: isTablet(),
                    [styles.Error]: hasError,
                    [styles.Disabled]: isDisabled,
                    [styles.WithUXSelection]: withUXSelection,
                    [styles.OnlyDeselectable]: isOnlyDeselectable,
                })}
            >
                <input
                    type="checkbox"
                    name={input?.name}
                    className={customClass?.Input}
                    id={input?.id}
                    value={input?.value}
                    disabled={isDisabled}
                    onClick={onClick}
                    checked={isChecked}
                    onChange={onChange}
                />
                <label className={customClass?.Label} htmlFor={input.id}>
                    {children}
                </label>
            </div>
        </ConditionalWrapper>
    );
};

export default Checkbox;
